/**
 * Helper functions for URL handling in production and development
 */

/**
 * Get the backend API base URL based on environment
 */
export const getBackendBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    // In development, we use the proxy configured in vue.config.js
    return '';
  }

  // Use VUE_APP_FLOWIT_URL as the backend URL
  const baseUrl = process.env.VUE_APP_FLOWIT_URL;
  if (!baseUrl) {
    console.error('VUE_APP_FLOWIT_URL environment variable is required in production');
    return '';
  }

  // Determine protocol - if URL already includes protocol, use it as is
  if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
    return baseUrl;
  }

  // Otherwise, add protocol
  const protocol = process.env.VUE_APP_FLOWIT_PROTOCOL || 'https';
  return `${protocol}://${baseUrl}`;
};

/**
 * Build complete URL for file/image resources
 * @param {string} path - The file path (can be relative or absolute)
 * @returns {string} Complete URL for the resource
 */
export const buildFileUrl = (path) => {
  // Handle URLs that may have incorrect domain (localhost:8080 instead of backend)
  if (path && (path.startsWith('http://') || path.startsWith('https://'))) {
    // If in development and URL points to frontend port (8080), fix it
    if (process.env.NODE_ENV === 'development') {
      // Extract the path from the URL
      const urlMatch = path.match(/https?:\/\/[^\/]+(\/.+)/);
      if (urlMatch && urlMatch[1]) {
        // Return just the path part for proxy handling
        return urlMatch[1];
      }
    }

    // Otherwise return as is for production
    return path;
  }

  // Get the backend base URL
  const backendUrl = getBackendBaseUrl();

  // If no backend URL in development (using proxy), return the path as is
  if (!backendUrl) {
    return path;
  }

  // Ensure path starts with /
  const cleanPath = path && path.startsWith('/') ? path : `/${path}`;

  // Return complete URL
  return `${backendUrl}${cleanPath}`;
};

/**
 * Get WebSocket URL based on environment
 */
export const getWebSocketUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    const wsProtocol = process.env.VUE_APP_WEBSOCKET_PROTOCOL || 'ws';
    const baseUrl = process.env.VUE_APP_FLOWIT_URL || 'localhost:3000';
    return `${wsProtocol}://${baseUrl}/cable`;
  }

  // In production, use secure websocket with the backend URL
  const backendUrl = getBackendBaseUrl();
  if (backendUrl) {
    // Replace https:// with wss:// or http:// with ws://
    return backendUrl.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:') + '/cable';
  }

  // Fallback
  const wsProtocol = process.env.VUE_APP_WEBSOCKET_PROTOCOL || 'wss';
  const baseUrl = process.env.VUE_APP_FLOWIT_URL;
  return `${wsProtocol}://${baseUrl}/cable`;
};

/**
 * Check if we're in a production environment
 */
export const isProduction = () => {
  return process.env.NODE_ENV === 'production' ||
         window.location.hostname !== 'localhost';
};

export default {
  getBackendBaseUrl,
  buildFileUrl,
  getWebSocketUrl,
  isProduction
};